import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  message,
  Button
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addPayroll, updatePayroll } from '@/api/payroll';

import { useAuth } from '@/contexts/AuthContext';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import CurrencyInput from '@/components/common/CurrencyInput';
import type { Payroll, PayrollStatus } from '@/types/employee/payroll';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import dayjs from 'dayjs';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const PAYROLL_STATUSES: { value: PayrollStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'processed', label: 'Processed' },
  { value: 'rejected', label: 'Rejected' }
];

const DEDUCTION_TYPES = [
  { value: 'tax', label: 'Tax' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'loan', label: 'Loan' },
  { value: 'other', label: 'Other' }
];

interface PayrollModalProps {
  open: boolean;
  payroll?: Payroll | null;
  onSuccess: () => void;
  onCancel: () => void;
}

// Form interface that includes employeeId for the selector
interface PayrollFormValues {
  employeeId?: string;
  status: PayrollStatus;
  period: {
    startDate: any;
    endDate: any;
  };
  grossSalary: any;
  netSalary: any;
  deductions: any[];
  notes?: string;
}

const PayrollModal: React.FC<PayrollModalProps> = ({
  open,
  payroll,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation(['payroll', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<PayrollFormValues>();

  const [employeeData, setEmployeeData] = useState<{ id: string; name: string } | null>(null);

  // Get current month start and end dates
  const currentMonthStart = dayjs().startOf('month');
  const currentMonthEnd = dayjs().endOf('month');

  useEffect(() => {
    if (open && payroll) {
      form.setFieldsValue({
        employeeId: payroll.employee.id, // Set the employeeId field for EmployeeSelector
        status: payroll.status,
        period: {
          startDate: payroll.period.startDate ? dayjs(formatFirestoreTimestamp(payroll.period.startDate)) : undefined,
          endDate: payroll.period.endDate ? dayjs(formatFirestoreTimestamp(payroll.period.endDate)) : undefined,
        },
        grossSalary: payroll.grossSalary,
        netSalary: payroll.netSalary,
        deductions: payroll.deductions || [],
        notes: payroll.notes,
      });
      
      setEmployeeData(payroll.employee);
      
    } else if (open && !payroll && !employeeData) {
      form.resetFields();
      form.setFieldsValue({
        status: 'pending' as PayrollStatus,
        deductions: [],
        period: {
          startDate: currentMonthStart,
          endDate: currentMonthEnd,
        },
        grossSalary: { amount: 0, currency: 'USD' },
        netSalary: { amount: 0, currency: 'USD' },
      });
      
      setEmployeeData(null);
    } else if (open && !payroll && employeeData) {
      form.setFieldsValue({
        employeeId: employeeData.id,
        status: 'pending' as PayrollStatus,
        deductions: [],
        period: {
          startDate: currentMonthStart,
          endDate: currentMonthEnd,
        },
        grossSalary: { amount: 0, currency: 'USD' },
        netSalary: { amount: 0, currency: 'USD' },
      });
    }
  }, [open, payroll, form, currentMonthStart, currentMonthEnd, employeeData]);

  const addPayrollMutation = useMutation({
    mutationFn: (data: any) => addPayroll(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('payroll:createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const updatePayrollMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updatePayroll(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('payroll:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['payrolls'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!employeeData) {
        message.error(t('payroll:employeeRequired'));
        return;
      }

      const basePayload: any = {
        ...values,
        employee: employeeData,
        period: {
          startDate: values.period.startDate && typeof (values.period.startDate as any).toDate === 'function' 
            ? (values.period.startDate as any).toDate() 
            : values.period.startDate,
          endDate: values.period.endDate && typeof (values.period.endDate as any).toDate === 'function' 
            ? (values.period.endDate as any).toDate() 
            : values.period.endDate,
        },
        grossSalary: values.grossSalary,
        netSalary: values.netSalary,
      };

      const payload = removeUndefined(basePayload);
      
      if (payroll) {
        await updatePayrollMutation.mutateAsync({ id: payroll.id, data: payload });
      } else {
        await addPayrollMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setEmployeeData(null);
    onCancel();
  };

  const isLoading = addPayrollMutation.isPending || updatePayrollMutation.isPending;

  return (
    <Modal
      title={payroll ? t('payroll:editPayroll') : t('payroll:addPayroll')}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={payroll ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isLoading}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'pending' as PayrollStatus,
          deductions: [],
          period: {
            startDate: currentMonthStart,
            endDate: currentMonthEnd,
          },
          grossSalary: { amount: 0, currency: 'USD' },
          netSalary: { amount: 0, currency: 'USD' },
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <EmployeeSelector
              disabled={isLoading}
              onEmployeeDataChange={setEmployeeData}
            />
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('payroll:statusLabel')}
              rules={[{ required: true, message: t('payroll:statusRequired') }]}
            >
              <Select placeholder={t('payroll:selectStatus')}>
                {PAYROLL_STATUSES.map(status => (
                  <Option key={status.value} value={status.value}>{status.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['period', 'startDate']}
              label={t('payroll:startDate')}
              rules={[{ required: true, message: t('payroll:startDateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['period', 'endDate']}
              label={t('payroll:endDate')}
              rules={[{ required: true, message: t('payroll:endDateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="grossSalary"
              label={t('payroll:grossSalary')}
              rules={[{ required: true, message: t('payroll:grossSalaryRequired') }]}
            >
              <CurrencyInput disabled={isLoading} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="netSalary"
              label={t('payroll:netSalary')}
              rules={[{ required: true, message: t('payroll:netSalaryRequired') }]}
            >
              <CurrencyInput disabled={isLoading} />
            </Form.Item>
          </Col>
        </Row>

        <Form.List name="deductions">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Row gutter={16} key={key} style={{ marginBottom: 16 }}>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'type']}
                      rules={[{ required: true, message: t('payroll:deductionTypeRequired') }]}
                    >
                      <Select placeholder={t('payroll:selectDeductionType')}>
                        {DEDUCTION_TYPES.map(type => (
                          <Option key={type.value} value={type.value}>{type.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'amount']}
                      rules={[{ required: true, message: t('payroll:deductionAmountRequired') }]}
                    >
                      <CurrencyInput disabled={isLoading} />
                    </Form.Item>
                  </Col>
                  <Col span={10}>
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      rules={[{ required: true, message: t('payroll:deductionDescriptionRequired') }]}
                    >
                      <Input placeholder={t('payroll:descriptionPlaceholder')} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {t('payroll:addDeduction')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Form.Item
          name="notes"
          label={t('payroll:notes')}
        >
          <TextArea rows={4} placeholder={t('payroll:notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PayrollModal; 